// Wrapper reactivo mínimo sobre supabase.auth para este panel.
// No se sube a @plataforma/supabase-client porque el patrón de suscripción
// (un solo listener global + notificación a quien se suscriba) es una
// decisión de estado de UI, no del cliente de datos — supabase-client se
// mantiene como lo que es: solo creación del cliente + lectura de claims
// (getClaims), sin opinión sobre cómo cada app reacciona a los cambios.

import { getClaims, supabaseClient } from '@plataforma/supabase-client';

import { navegarA } from './enrutador.js';

/** @type {Set<(estado: EstadoSesion) => void>} */
const subscriptores = new Set();

/**
 * @typedef {object} EstadoSesion
 * @property {import('@supabase/supabase-js').Session | null} sesion
 * @property {ReturnType<typeof getClaims>} claims
 * @property {boolean} cargando
 */

/** @type {EstadoSesion} */
let estadoActual = { sesion: null, claims: { empresa_id: null, rol: null }, cargando: true };

function notificar() {
  for (const fn of subscriptores) fn(estadoActual);
}

/**
 * Inicializa la escucha de cambios de sesión. Debe llamarse una sola vez al
 * arrancar la app (main.js), antes de montar el router.
 */
export async function inicializarSesion() {
  const {
    data: { session },
  } = await supabaseClient().auth.getSession();
  estadoActual = { sesion: session, claims: getClaims(session), cargando: false };
  notificar();

  supabaseClient().auth.onAuthStateChange((evento, session) => {
    const eventosQueCambianEstado = new Set([
      'SIGNED_IN',
      'SIGNED_OUT',
      'USER_UPDATED',
      'PASSWORD_RECOVERY',
    ]);

    if (eventosQueCambianEstado.has(evento)) {
      estadoActual = { sesion: session, claims: getClaims(session), cargando: false };
      notificar();
    } else if (evento === 'TOKEN_REFRESHED' && session) {
      // Actualizar tokens internos SIN disparar re-render: el usuario sigue
      // autenticado y sus claims no cambian. Evita re-renders innecesarios
      // (p. ej. si en el futuro hay inputs de archivo en este panel).
      estadoActual = { ...estadoActual, sesion: session, claims: getClaims(session) };
    }

    if (evento === 'PASSWORD_RECOVERY') {
      // Ver estado-sesion.js de panel-cliente para la explicación completa
      // de por qué esta navegación explícita es necesaria (colisión entre
      // el hash de tokens de Supabase y el router basado en hash de esta
      // app).
      navegarA('/cambiar-contrasena');
    }
  });
}

/** @returns {EstadoSesion} */
export function obtenerEstadoSesion() {
  return estadoActual;
}

/**
 * @param {(estado: EstadoSesion) => void} fn
 * @returns {() => void} función para cancelar la suscripción
 */
export function suscribirseASesion(fn) {
  subscriptores.add(fn);
  return () => subscriptores.delete(fn);
}

/**
 * @param {string} email
 * @param {string} password
 */
export async function iniciarSesion(email, password) {
  return supabaseClient().auth.signInWithPassword({ email, password });
}

export async function cerrarSesion() {
  return supabaseClient().auth.signOut();
}

/**
 * @param {string} nuevaContrasena
 * Ver estado-sesion.js de panel-cliente: mismo helper, misma justificación.
 */
export async function actualizarContrasena(nuevaContrasena) {
  return supabaseClient().auth.updateUser({ password: nuevaContrasena });
}

/**
 * @param {string} email
 * Ver estado-sesion.js de panel-cliente: mismo helper, misma justificación.
 */
export async function solicitarRecuperacionContrasena(email) {
  return supabaseClient().auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin,
  });
}
